import { useEffect, useState } from "react";

function useTheme() {
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof localStorage === "undefined") return "light";
    const stored = localStorage.getItem("theme");
    return stored === "dark" ? "dark" : "light";
  });
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    try {
      localStorage.setItem("theme", theme);
    } catch {}
  }, [theme]);
  return { theme, toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")) };
}

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";

  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="relative h-7 w-14 shrink-0 overflow-hidden rounded-full border border-border transition-colors duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      style={{
        background: isDark
          ? "linear-gradient(135deg, #0b1230 0%, #1a2350 100%)"
          : "linear-gradient(135deg, #7dd3fc 0%, #bae6fd 100%)",
      }}
    >
      {/* Stars (dark) */}
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-0 transition-opacity duration-500 ${
          isDark ? "opacity-100" : "opacity-0"
        }`}
      >
        <span className="absolute left-[20px] top-[6px] h-[2px] w-[2px] rounded-full bg-white" />
        <span className="absolute left-[32px] top-[14px] h-[1.5px] w-[1.5px] rounded-full bg-white/80" />
        <span className="absolute left-[42px] top-[5px] h-[2px] w-[2px] rounded-full bg-white" />
        <span className="absolute left-[48px] top-[18px] h-[1.5px] w-[1.5px] rounded-full bg-white/70" />
        <span className="absolute left-[26px] top-[20px] h-[1px] w-[1px] rounded-full bg-white/60" />
      </span>

      {/* Clouds (light) */}
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-0 transition-opacity duration-500 ${
          isDark ? "opacity-0" : "opacity-100"
        }`}
      >
        <span className="absolute left-[6px] top-[14px] h-[5px] w-[10px] rounded-full bg-white/90" />
        <span className="absolute left-[10px] top-[10px] h-[4px] w-[7px] rounded-full bg-white/80" />
        <span className="absolute left-[22px] top-[18px] h-[4px] w-[8px] rounded-full bg-white/70" />
      </span>

      {/* Sliding orb: moon (left) in dark, sun (right) in light */}
      <span
        aria-hidden
        className="absolute top-1/2 grid h-[22px] w-[22px] -translate-y-1/2 place-items-center rounded-full shadow-md transition-all duration-500 ease-out"
        style={{
          left: isDark ? "3px" : "calc(100% - 25px)",
          background: isDark
            ? "radial-gradient(circle at 35% 35%, #e5e7eb, #9ca3af)"
            : "radial-gradient(circle at 35% 35%, #fde68a, #f59e0b)",
          boxShadow: isDark
            ? "0 0 6px rgba(200,200,220,0.4)"
            : "0 0 10px rgba(253,224,71,0.7)",
        }}
      >
      </span>
    </button>
  );
}
